import {
  createSlatePlugin,
  pluginDeserializeHtml,
  type SlateEditor,
  type TElement,
} from "platejs";
import { toNodeProps } from "./withNodeProps";

// A table without an explicit <tbody> gets one inserted implicitly by the HTML parser (per the
// HTML5 parsing spec), wrapping its <tr> rows. Plate's table model has no distinct tbody node -
// TablePlugin's own renderer always adds a literal <tbody> wrapper on save - so treating an
// unrecognized <tbody>/<thead>/<tfoot> as regular preserved content double-wraps (and, since
// Plate's table normalizer lifts <tr> out of any non-table-aware wrapper, leaves an empty shell
// behind). These tags are never independently meaningful outside a <table>, so defer to letting
// them unwrap transparently instead of preserving them, exactly like before this plugin existed.
const TABLE_STRUCTURE_NODE_NAMES = new Set(["TBODY", "THEAD", "TFOOT"]);

// Bold, italic, underline, code, highlight, and font-color/size spans have no element plugin of
// their own - they're normally applied via Plate's separate mark/leaf pass, which only runs when no
// element plugin has already claimed the tag. Element plugins that would otherwise match such a tag
// must defer to it so that pass still runs. A leaf plugin can declare itself leaf-only either on
// `node.isLeaf` (Bold, Italic, ...) or on `parsers.html.deserializer.isLeaf` (FontColor, FontSize,
// ...) - check both.
export function isLeafHtmlElement(editor: SlateEditor, element: HTMLElement) {
  return editor.meta.pluginList.some((plugin) => {
    const isLeafPlugin =
      plugin.node?.isLeaf || plugin.parsers?.html?.deserializer?.isLeaf;
    return (
      isLeafPlugin &&
      pluginDeserializeHtml(editor, plugin, {
        deserializeLeaf: true,
        element,
      })
    );
  });
}

export type TUnknownElement = TElement & { tagName: string };

// Rendered as inline voids so they stay inside the surrounding paragraph instead of splitting it.
export const INLINE_UNKNOWN_TAG_NAMES = new Set(["wbr", "svg", "math"]);

// Global fallback for any HTML tag with no registered plugin (video, audio, embed, object,
// svg, canvas, form, section, figure, etc) - deserialize would otherwise silently drop the tag
// on save. It's an opaque void block (like ScriptPlugin/ScriptPlaceholder): the whole subtree,
// including any recognized content nested inside it (a <p> inside an unregistered <section>),
// is captured as a single raw-HTML blob and shown as a placeholder (see
// UnknownElementPlaceholder / base-plate-component-overrides.tsx) rather than parsed into
// individually editable Slate nodes - reconstructed byte-for-byte on save. Being void, it's
// click-to-select and backspace-deletable like any other void element (an image, an iframe) -
// that's fine: the placeholder makes it visible, so removing it is a deliberate user action,
// not silent data loss the way dropping the tag on save used to be.
//
// Priority is raised above the default (100) so every other plugin gets first chance to claim
// a tag - Plate resolves html deserialize rules by testing lower-priority plugins first, so a
// high priority here is what makes this a true last-resort catch-all instead of hijacking tags
// other plugins already handle.
export const UnknownElementPlugin = createSlatePlugin({
  key: "unknown-html-element",
  priority: 1000,
  node: {
    isElement: true,
    isVoid: true,
  },
  parsers: {
    html: {
      deserializer: {
        rules: [{ validNodeName: "*" }],
        withoutChildren: true,
        query: ({ editor, element }) => {
          if (TABLE_STRUCTURE_NODE_NAMES.has(element.nodeName)) return false;
          return !isLeafHtmlElement(editor, element);
        },
        parse: ({ element, type }) => ({
          type,
          tagName: element.tagName.toLowerCase(),
        }),
        toNodeProps: (params) => {
          const nodeProps = toNodeProps(params);
          return {
            ...nodeProps,
            elementAttributes: {
              ...nodeProps.elementAttributes,
              dangerouslySetInnerHTML: { __html: params.element.innerHTML },
            },
          };
        },
      },
    },
  },
}).overrideEditor(({ api: { isInline }, type }) => ({
  api: {
    isInline(element) {
      if (element.type === type) {
        return INLINE_UNKNOWN_TAG_NAMES.has(
          (element as TUnknownElement).tagName,
        );
      }
      return isInline(element);
    },
  },
}));
